import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import type { DateRange } from 'react-day-picker';
import { RotateCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DateRangePicker } from '@/components/shared/DateRangePicker';
import { CopyValueButton } from '@/components/shared/CopyValueButton';
import { isRowNavHandled } from '@/components/shared/rowNav';
import { FiscalReceiptStatusBadge } from '@/components/fiscal/FiscalStatusBadge';
import { FiscalAccessDenied } from '@/components/fiscal/FiscalAccessDenied';
import { shortenMiddle } from '@/components/fiscal/fiscalFormat';
import { FiscalReceiptDetailDialog } from './FiscalReceiptDetailDialog';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useFiscalApi, type FiscalReceiptListItem } from '@/services/api';
import { computeFiscalReceiptTotal } from '@/utils/fiscalAmounts';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import { FISCAL_RECEIPT_STATUSES, type FiscalPrinterDto, type FiscalReceiptStatus } from '@ip/shared-types';

const POLL_MS = 30_000;
const LIMIT_OPTIONS = [25, 50, 100, 200] as const;

/** Fiscal receipt list — read-only passthrough table with status/printer/date filters,
 *  30 s auto-refresh, and a row-click detail dialog (PDF + retry). */
export function FiscalReceipts() {
  const { nip } = useOutletContext<AppShellContext>();
  const { listReceipts, listPrinters } = useFiscalApi();
  const [receipts, setReceipts] = useState<FiscalReceiptListItem[]>([]);
  const [printers, setPrinters] = useState<FiscalPrinterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [status, setStatus] = useState<FiscalReceiptStatus | 'all'>('all');
  const [printerId, setPrinterId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [limit, setLimit] = useState<number>(50);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const loadSeq = useRef(0);

  useEffect(() => {
    listPrinters(nip)
      .then(setPrinters)
      .catch(() => {
        /* printer filter just stays empty — the list itself still works */
      });
  }, [nip, listPrinters]);

  const printerCodes = useMemo(
    () => Object.fromEntries(printers.map((p) => [p.id, p.code])),
    [printers],
  );

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await listReceipts(nip, {
        status: status === 'all' ? undefined : status,
        printerId: printerId === 'all' ? undefined : printerId,
        from: dateRange?.from ? dayjs(dateRange.from).format('YYYY-MM-DD') : undefined,
        to: dateRange?.to ? dayjs(dateRange.to).format('YYYY-MM-DD') : undefined,
        limit,
      });
      if (seq !== loadSeq.current) return;
      setReceipts(result);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(t('errors.loadingFailed'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, status, printerId, dateRange, limit, listReceipts]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const rows = useMemo(
    () =>
      receipts.map((rec) => {
        let total: number | null = null;
        try {
          total = computeFiscalReceiptTotal(rec.receipt);
        } catch {
          total = null;
        }
        return { rec, total };
      }),
    [receipts],
  );

  if (forbidden) return <FiscalAccessDenied />;

  return (
    <div className="flex flex-col gap-3">
      <Card className="flex flex-wrap items-end gap-3 p-3">
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('fiscal.columns.status')}</p>
          <Select value={status} onValueChange={(v) => setStatus(v as FiscalReceiptStatus | 'all')}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('fiscal.invoices.statusAll')}</SelectItem>
              {FISCAL_RECEIPT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`fiscal.receiptStatus.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('fiscal.columns.printer')}</p>
          <Select value={printerId} onValueChange={setPrinterId}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('fiscal.receipts.printerAll')}</SelectItem>
              {printers.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('common.filter')}</p>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('fiscal.invoices.limitLabel')}</p>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="w-[90px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIMIT_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label={t('fiscal.receipts.refresh')}>
          <RotateCw className="size-4" />
        </Button>
      </Card>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t('fiscal.columns.status')}</TableHead>
              <TableHead>{t('fiscal.columns.mode')}</TableHead>
              <TableHead>{t('fiscal.columns.printer')}</TableHead>
              <TableHead className="text-right">{t('fiscal.columns.amount')}</TableHead>
              <TableHead>{t('fiscal.columns.jpkid')}</TableHead>
              <TableHead>{t('fiscal.columns.createdAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-40 text-center">
                  <p className="font-medium">{t('fiscal.receipts.empty.title')}</p>
                  <p className="text-muted-foreground text-sm">{t('fiscal.receipts.empty.description')}</p>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(({ rec, total }) => (
                <TableRow
                  key={rec.uuid}
                  className="group cursor-pointer"
                  onClick={(e) => {
                    if (isRowNavHandled(e)) return;
                    setSelectedUuid(rec.uuid);
                  }}
                >
                  <TableCell>
                    <FiscalReceiptStatusBadge status={rec.status} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal">
                      {t(`fiscal.receipts.mode.${rec.mode === 'paper' ? 'paper' : 'eparagon'}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>{(rec.printerId && printerCodes[rec.printerId]) ?? '—'}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {total === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      `${formatPLN(total)} PLN`
                    )}
                  </TableCell>
                  <TableCell>
                    {rec.jpkid ? (
                      <span className="inline-flex items-center gap-1 font-mono text-xs">
                        {shortenMiddle(rec.jpkid)}
                        <CopyValueButton value={rec.jpkid} />
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                    {new Date(rec.createdAt).toLocaleString('pl-PL')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FiscalReceiptDetailDialog
        nip={nip}
        uuid={selectedUuid}
        printerCodes={printerCodes}
        onOpenChange={(open) => !open && setSelectedUuid(null)}
        onChanged={load}
      />
    </div>
  );
}
